/**
 * SELL-05 order fulfilment flow (PRD S-05): new → accepted → packed →
 * dispatched → delivered, with `exception` reachable from any open state.
 * Each step is a declaration (what it needs, where it goes), not a switch.
 */
import { serialMatches } from './serial';

export const ORDER_QUEUES = ['new', 'accepted', 'packed', 'dispatched', 'delivered', 'returns', 'exception'];

export const COURIERS = ['Blue Dart', 'Delhivery', 'DTDC', 'Ekart', 'Shiprocket', 'India Post'];

/** step key → { from, to, label, check(order, input) → error message | '' } */
export const ORDER_STEPS = {
  accept: {
    from: ['new'], to: 'accepted', label: 'Accept order',
    check: () => '',
  },
  pick: {
    from: ['accepted'], to: 'accepted', label: 'Confirm picked unit',
    check: (order, input = {}) => (serialMatches(input.scan, order.unit) ? '' : 'Scanned serial does not match the unit on this order'),
  },
  pack: {
    from: ['accepted'], to: 'packed', label: 'Mark packed',
    check: (order, input = {}) => {
      if (!order.picked_at) return 'Scan and confirm the exact unit first';
      if (!input.photos || input.photos < 1) return 'Add at least one pack photo as evidence';
      return '';
    },
  },
  dispatch: {
    from: ['packed'], to: 'dispatched', label: 'Dispatch',
    check: (order, input = {}) => {
      if (!input.courier) return 'Pick a courier';
      if (!/^[A-Z0-9-]{6,30}$/i.test(String(input.tracking || '').trim())) return 'Enter a valid tracking number';
      return '';
    },
  },
  deliver: {
    from: ['dispatched'], to: 'delivered', label: 'Mark delivered',
    check: () => '',
  },
  exception: {
    from: ['new', 'accepted', 'packed', 'dispatched'], to: 'exception', label: 'Raise exception',
    check: (order, input = {}) => (String(input.reason || '').trim().length >= 5 ? '' : 'Give a reason (at least 5 characters)'),
  },
  // Returns (server: lib/commerce/fulfilment.js). Display rules only; the server decides.
  approve_return: {
    from: ['return_requested'], to: 'return_in_transit', label: 'Approve return',
    check: (order, input = {}) => (input.tracking && !/^[A-Z0-9-]{6,30}$/i.test(String(input.tracking).trim()) ? 'Enter a valid pickup tracking number, or leave it blank' : ''),
  },
  reject_return: {
    from: ['return_requested'], to: 'delivered', label: 'Reject return',
    check: (order, input = {}) => (String(input.reason || '').trim().length >= 5 ? '' : 'Tell the customer why (at least 5 characters)'),
  },
  receive_return: {
    from: ['return_in_transit'], to: 'returned', label: 'Receive return',
    check: (order, input = {}) => (['pass', 'fail'].includes(input.result) ? '' : 'Record the return QC result'),
  },
};

/**
 * Acceptance SLA (MERCH-58): time left until `accept_by` for a new order.
 * → null (not a new order / no deadline), or { overdue, label, urgent } — urgent under 2 hours.
 */
export function acceptCountdown(order, now = Date.now()) {
  if (!order || order.status !== 'new' || !order.accept_by) return null;
  const left = new Date(order.accept_by).getTime() - now;
  if (Number.isNaN(left)) return null;
  if (left <= 0) return { overdue: true, urgent: true, label: 'Past cutoff' };
  const mins = Math.floor(left / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return { overdue: false, urgent: left < 2 * 3600000, label: h ? `${h}h ${m}m left` : `${m}m left` };
}

/** Steps available for an order in its current status. */
export function availableSteps(order) {
  if (!order) return [];
  return Object.entries(ORDER_STEPS)
    .filter(([key, s]) => s.from.includes(order.status) && !(key === 'pick' && order.picked_at))
    .map(([key]) => key);
}

/** Apply a step: returns { ok, order?, error? } without mutating the input. */
export function applyStep(order, key, input = {}, at = new Date().toISOString()) {
  const step = ORDER_STEPS[key];
  if (!step) return { ok: false, error: `Unknown step "${key}"` };
  if (!step.from.includes(order.status)) return { ok: false, error: `Cannot ${step.label.toLowerCase()} an order that is ${order.status}` };
  const error = step.check(order, input);
  if (error) return { ok: false, error };
  const next = { ...order, status: step.to, timeline: [...(order.timeline || [])] };
  const extra = {
    pick: () => ({ picked_at: at }),
    pack: () => ({ packed_at: at, pack_photos: input.photos }),
    dispatch: () => ({ courier: input.courier, tracking: String(input.tracking).trim().toUpperCase() }),
    exception: () => ({ exception_reason: String(input.reason).trim() }),
  }[key];
  Object.assign(next, extra ? extra() : {});
  const notes = {
    accept: 'Order accepted',
    pick: `Unit ${order.unit && order.unit.serial_masked} scanned and confirmed`,
    pack: `Packed with ${input.photos} photo${input.photos === 1 ? '' : 's'} as evidence`,
    dispatch: `Dispatched via ${input.courier} · ${next.tracking}`,
    deliver: 'Delivered to customer',
    exception: `Exception: ${next.exception_reason}`,
  };
  next.timeline.push({ at, status: next.status, note: notes[key] });
  return { ok: true, order: next };
}

/* ------------------------------------------------------------------ quantity stock (D60/D61) */

export const isLotLine = (u) => Boolean(u) && u.stock_mode === 'quantity';
const normSku = (v) => String(v || '').trim().toUpperCase().replace(/\s+/g, '');
/** A stock lot is picked by its SKU (the server checks the count); a unit by its serial (see serialMatches). */
export const skuMatches = (scan, u) => isLotLine(u) && normSku(scan) !== '' && normSku(scan) === normSku(u.sku);

